import { DateTime } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import { useGetInvestmentForecast } from '../../../api/pension-robot/useGetInvestmentForecast';
import { useGetPensionCalcData } from '../../../api/pension-robot/useGetPensionCalcData';
import { DEFAULT_PENSION_AGE, MIN_AGE_CLIENT } from '../../../config/constants';
import { Account, PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { getResultSavingsAccount, getSavingUntilPensionAge } from '../../../utils/functions';

export const useAccountBalancePrediction = (
    currentAccount: Account | undefined,
    onlyAvailableAtPensionDate: boolean,
    isPartner?: boolean,
    enforceCalculation?: boolean,
) => {
    const { parameters: {personalInfo} } = useContext(PensionContext);
    const pensionCalcData = useGetPensionCalcData();
    const yearlyMarketRate = pensionCalcData.data?.marketRate ?? 0;
    const legalRetirementAge = pensionCalcData.data?.legalRetirementAge ?? DEFAULT_PENSION_AGE;
    const pensionAge = isPartner
        ? personalInfo.partner?.pensionAge ?? legalRetirementAge
        : personalInfo.self?.pensionAge ?? legalRetirementAge;

    const currentAge = isPartner
        ? personalInfo.partner?.age ?? MIN_AGE_CLIENT
        : personalInfo.self?.age ?? MIN_AGE_CLIENT;

    const accountAvailableAtDateTime = currentAccount?.availableAt
        ? DateTime.fromISO(currentAccount.availableAt).plus({day:1})
        : undefined;
    const diffNowAndEndOfAccountInYears = accountAvailableAtDateTime
        ? accountAvailableAtDateTime?.diff(DateTime.now().startOf('day').plus({day:1}), 'years').years
        : pensionAge - currentAge;
    const recurringPaymentDurationInMonths = accountAvailableAtDateTime
        ? Math.ceil(Math.abs(DateTime.now().diff(accountAvailableAtDateTime, 'months').months))
        : undefined;

    const [investmentForecast, setInvestmentForecast] = useState(0);

    const getAccountEndDate = () => {
        if (currentAccount?.availableAt)
            return currentAccount?.availableAt

        const yearsUntilPension = Math.max(pensionAge - currentAge, 0);
        const dateOfPension = DateTime.now().plus({
            years: yearsUntilPension
        }).toISODate()
        return dateOfPension;
    }

    const accountEndDate = getAccountEndDate();

    const ageAtEndOfAccount = currentAge + diffNowAndEndOfAccountInYears;
    const yearsFromAccountEndToPension = pensionAge - ageAtEndOfAccount;

    const investmentForecastApiCall = useGetInvestmentForecast(
        {
            riskProfileId: Number.parseInt(currentAccount?.riskProfile ?? '10') || 10,
            paymentSingleAmount: currentAccount?.currentAmount,
            paymentRecurringAmount: currentAccount?.recurringPaymentAmount,
            paymentRecurringInterval: currentAccount?.recurringPaymentInterval === 'month' ? 'Month' : 'Year',
            investmentEndDate: accountEndDate,
            paymentRecurringEndDate: accountEndDate,
            automaticRiskReduction: true,
            calculationMode: onlyAvailableAtPensionDate ? 'NewProduct' : 'ExistingProduct'
        },
        currentAccount?.type === 'investment' && formCompleted(currentAccount, onlyAvailableAtPensionDate)
    );

    const calculateAmountAtPensionAgeAfterAccountEnds = (amountAtEndOfAccount: number) => {
        const amountAtPensionAge = getSavingUntilPensionAge(
            amountAtEndOfAccount,
            yearsFromAccountEndToPension,
            yearlyMarketRate
        );
        return amountAtPensionAge;
    }

    useEffect(() => {
        if (!!investmentForecastApiCall) {
            const investmentForcast = investmentForecastApiCall.data?.forecastExpirationValues?.median ?? 0;
            setInvestmentForecast(investmentForcast);
        }
    }, [investmentForecastApiCall, currentAccount, setInvestmentForecast]); 

    const [savingForecast, setSavingForecast] = useState(0);

    useEffect(() => {
        if ( currentAccount?.type === 'saving' && !formCompleted(currentAccount, onlyAvailableAtPensionDate)){
            return;
        }
        const forecast = getResultSavingsAccount(
            currentAccount?.currentAmount ?? 0,
            currentAccount?.recurringPaymentAmount ?? 0,
            currentAccount?.recurringPaymentInterval ?? 'month',
            diffNowAndEndOfAccountInYears,
            yearlyMarketRate,
            recurringPaymentDurationInMonths
        );
        setSavingForecast(forecast);
    }, [currentAccount,
        onlyAvailableAtPensionDate,
        diffNowAndEndOfAccountInYears,
        setSavingForecast,
        recurringPaymentDurationInMonths,
        yearlyMarketRate]);

    const computationResult = currentAccount?.type === 'saving' 
        ? savingForecast
        : investmentForecast;

    // If not available at date, and it's not an additional account its pension should not be
    // calculated, hence 0. Still the amount at end account should be available for display.
    if ( !enforceCalculation && currentAccount?.changedManually && !currentAccount?.availableAt ){
        const computationResultManuallySet = currentAccount?.predictedAmountAtEndAccount ?? 0;
        return {
            computationResultAtPension: 0,
            computationResultAtAccountEnd: computationResultManuallySet,
            isInvestmentLoading: false,
            isInvestmentError: false,
            isInvestmentFetching: false,
            isInvestmentIdle: false
        };
    };

    if ( !enforceCalculation && currentAccount?.changedManually ){
        const computationResultManuallySet = currentAccount?.predictedAmountAtEndAccount ?? 0;
        return {
            computationResultAtPension: calculateAmountAtPensionAgeAfterAccountEnds(computationResultManuallySet),
            computationResultAtAccountEnd: computationResultManuallySet,
            isInvestmentLoading: false,
            isInvestmentError: false,
            isInvestmentFetching: false,
            isInvestmentIdle: false
        };
    };

    return { 
        computationResultAtPension: calculateAmountAtPensionAgeAfterAccountEnds(computationResult),
        computationResultAtAccountEnd: computationResult,
        isInvestmentLoading: investmentForecastApiCall.isLoading,
        isInvestmentError: investmentForecastApiCall.isError,
        isInvestmentFetching: investmentForecastApiCall.isFetching,
        isInvestmentIdle: investmentForecastApiCall.isIdle
    };
};

export const formCompleted = (currentAccount: Account, onlyAvailableAtPensionDate: boolean) =>
    ( !!currentAccount.availableAt || onlyAvailableAtPensionDate)
    && currentAccount.currentAmount !== undefined
    && currentAccount.recurringPaymentAmount !== undefined
    && currentAccount.recurringPaymentInterval !== undefined